import {useCallback, useMemo, type ChangeEvent, type SyntheticEvent, type ReactNode} from 'react'
import {Typography, Box, TextField, Select, MenuItem, Checkbox, FormControlLabel, ListSubheader} from '@mui/material'
import AspectRatioIcon from '@mui/icons-material/AspectRatio'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  setExportWidth,
  setExportHeight,
  setExportSize,
  setExportFrameEnabled,
  setExportCarouselEnabled,
  setExportCarouselCount,
  setExportCarouselGap,
  selectActiveScene,
} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {
  EXPORT_PRESETS,
  EXPORT_PRESET_GROUPS,
  CUSTOM_PRESET_ID,
  MAX_EXPORT_DIMENSION,
  MIN_EXPORT_DIMENSION,
  MIN_CAROUSEL_COUNT,
  MAX_CAROUSEL_COUNT,
  MIN_CAROUSEL_GAP_PCT,
  MAX_CAROUSEL_GAP_PCT,
} from '../../../common/constants/exportPresets'
import type {ExportPreset} from '../../../common/constants/exportPresets'
import SliderRow from '../sharedWidgets/SliderRow'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Helpers ───────────────────────────────────────────────────────

/** Find matching preset for current width/height */
const findPresetId = (w: number, h: number): string => {
  const match = EXPORT_PRESETS.find((p) => p.width === w && p.height === h)
  return match ? match.id : CUSTOM_PRESET_ID
}

/** Group presets by their group label */
const groupedPresetItems = (): ReactNode[] => {
  const items: ReactNode[] = []
  for (const group of EXPORT_PRESET_GROUPS) {
    items.push(
      <ListSubheader key={`group-${group}`} sx={{fontSize: 11, lineHeight: '28px'}}>
        {group}
      </ListSubheader>,
    )
    const presets = EXPORT_PRESETS.filter((p) => p.group === group)
    for (const preset of presets) {
      items.push(
        <MenuItem key={preset.id} value={preset.id} sx={{fontSize: 12}}>
          {preset.label} ({preset.width}×{preset.height})
        </MenuItem>,
      )
    }
  }
  return items
}

// ── ExportResolutionPanel — export resolution sub-accordion ───────

const ExportResolutionPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)
  const exportFrameEnabled = useAppSelector((s) => selectActiveScene(s).exportFrameEnabled)
  const exportCarouselEnabled = useAppSelector((s) => selectActiveScene(s).exportCarouselEnabled)
  const exportCarouselCount = useAppSelector((s) => selectActiveScene(s).exportCarouselCount)
  const exportCarouselGap = useAppSelector((s) => selectActiveScene(s).exportCarouselGap)

  const selectedPresetId = findPresetId(exportWidth, exportHeight)

  const handlePresetChange = useCallback(
    (e: {target: {value: string}}) => {
      const id = e.target.value
      if (id === CUSTOM_PRESET_ID) return
      const preset = EXPORT_PRESETS.find((p: ExportPreset) => p.id === id)
      if (preset) {
        dispatch(setExportSize({width: preset.width, height: preset.height}))
      }
    },
    [dispatch],
  )

  const handleWidthChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10)
      if (!isNaN(value) && value > 0) dispatch(setExportWidth(value))
    },
    [dispatch],
  )

  const handleHeightChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10)
      if (!isNaN(value) && value > 0) dispatch(setExportHeight(value))
    },
    [dispatch],
  )

  const handleFrameToggle = useCallback(
    (_: SyntheticEvent, checked: boolean) => {
      dispatch(setExportFrameEnabled(checked))
      dispatch(sealUndo())
    },
    [dispatch],
  )

  const handleCarouselToggle = useCallback(
    (_: SyntheticEvent, checked: boolean) => {
      dispatch(setExportCarouselEnabled(checked))
      dispatch(sealUndo())
    },
    [dispatch],
  )

  const handleCarouselCountChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10)
      if (!isNaN(value)) dispatch(setExportCarouselCount(value))
    },
    [dispatch],
  )

  const handleCarouselGapChange = useCallback(
    (value: number) => {
      dispatch(setExportCarouselGap(value))
    },
    [dispatch],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const presetItems = useMemo(() => groupedPresetItems(), [])

  return (
    <Box>
      {/* Section header */}
      <Box sx={sx.sectionHeader}>
        <AspectRatioIcon sx={sx.subIcon} />
        <Typography sx={sx.subTitle}>{t('exportResolution.title')}</Typography>
      </Box>

      {/* Content */}
      <Box sx={sx.content}>
        {/* Preset selector */}
        <Box sx={sx.fieldRow}>
          <Typography sx={sx.fieldLabel}>{t('exportResolution.preset')}</Typography>
          <Select
            value={selectedPresetId}
            onChange={handlePresetChange}
            size="small"
            fullWidth
            sx={sx.select}
            MenuProps={{PaperProps: {sx: {maxHeight: 320}}}}
          >
            <MenuItem value={CUSTOM_PRESET_ID} sx={{fontSize: 12}}>
              {t('exportResolution.custom')}
            </MenuItem>
            {presetItems}
          </Select>
        </Box>

        {/* Width / Height inputs */}
        <Box sx={sx.dimensionRow}>
          <Box sx={sx.dimensionField}>
            <Typography sx={sx.fieldLabel}>{t('exportResolution.width')}</Typography>
            <TextField
              type="number"
              value={exportWidth}
              onChange={handleWidthChange}
              onBlur={handleSealUndo}
              size="small"
              slotProps={{htmlInput: {min: MIN_EXPORT_DIMENSION, max: MAX_EXPORT_DIMENSION}}}
              sx={sx.numberInput}
            />
          </Box>
          <Box sx={sx.dimensionField}>
            <Typography sx={sx.fieldLabel}>{t('exportResolution.height')}</Typography>
            <TextField
              type="number"
              value={exportHeight}
              onChange={handleHeightChange}
              onBlur={handleSealUndo}
              size="small"
              slotProps={{htmlInput: {min: MIN_EXPORT_DIMENSION, max: MAX_EXPORT_DIMENSION}}}
              sx={sx.numberInput}
            />
          </Box>
        </Box>

        {/* Export frame checkbox */}
        <FormControlLabel
          control={<Checkbox checked={exportFrameEnabled} onChange={handleFrameToggle} size="small" sx={sx.checkbox} />}
          label={<Typography sx={sx.checkboxLabel}>{t('exportResolution.exportFrame')}</Typography>}
          sx={sx.formControl}
        />

        {/* Carousel checkbox + count input */}
        <FormControlLabel
          control={
            <Checkbox checked={exportCarouselEnabled} onChange={handleCarouselToggle} size="small" sx={sx.checkbox} />
          }
          label={<Typography sx={sx.checkboxLabel}>{t('exportResolution.carousel')}</Typography>}
          sx={sx.formControl}
        />
        {exportCarouselEnabled && (
          <Box sx={sx.carouselRow}>
            <Typography sx={sx.fieldLabel}>{t('exportResolution.carouselCount')}</Typography>
            <TextField
              type="number"
              value={exportCarouselCount}
              onChange={handleCarouselCountChange}
              onBlur={handleSealUndo}
              size="small"
              slotProps={{htmlInput: {min: MIN_CAROUSEL_COUNT, max: MAX_CAROUSEL_COUNT, step: 1}}}
              sx={sx.carouselInput}
            />
            <Typography sx={sx.carouselHint}>
              {t('exportResolution.carouselHint', {count: exportCarouselCount})}
            </Typography>
          </Box>
        )}
        {exportCarouselEnabled && (
          <Box sx={sx.carouselGapBlock}>
            <SliderRow
              label={t('exportResolution.carouselGap')}
              value={exportCarouselGap}
              min={MIN_CAROUSEL_GAP_PCT}
              max={MAX_CAROUSEL_GAP_PCT}
              step={1}
              unit="%"
              onChange={handleCarouselGapChange}
              onChangeCommitted={handleSealUndo}
            />
            <Typography sx={sx.carouselHint}>{t('exportResolution.carouselGapHint')}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 4px 8px',
  },
  subIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 16,
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.secondary,
  },
  content: {
    padding: '0 4px 12px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  fieldRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  fieldLabel: {
    fontSize: 11,
    color: tokens.text.muted,
    fontWeight: 500,
  },
  select: {
    fontSize: 12,
    color: tokens.text.primary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.text.muted,
    },
  },
  dimensionRow: {
    display: 'flex',
    gap: '8px',
  },
  dimensionField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  numberInput: {
    '& .MuiInputBase-input': {
      fontSize: 12,
      color: tokens.text.primary,
      padding: '6px 8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.text.muted,
    },
  },
  checkbox: {
    color: tokens.text.muted,
    '&.Mui-checked': {
      color: tokens.text.primary,
    },
  },
  checkboxLabel: {
    fontSize: 12,
    color: tokens.text.secondary,
  },
  formControl: {
    marginLeft: '-4px',
  },
  carouselRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: '24px',
    marginTop: '-4px',
    flexWrap: 'wrap' as const,
  },
  carouselInput: {
    width: 64,
    '& .MuiInputBase-input': {
      fontSize: 12,
      color: tokens.text.primary,
      padding: '4px 8px',
      textAlign: 'center' as const,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.text.muted,
    },
  },
  carouselHint: {
    fontSize: 10,
    color: tokens.text.muted,
    flex: '1 1 100%',
    marginTop: '2px',
    lineHeight: 1.3,
  },
  carouselGapBlock: {
    marginLeft: '24px',
    marginTop: '4px',
  },
})

export default ExportResolutionPanel
